/**
 * checkOutcome — правило «сбой проверки ≠ отрицательный ответ»
 * (решение владельца 23.08.2026, комментарий к WEB-1523).
 *
 * Формулировка владельца дословно: «Сбой сервера показывать честно. Когда
 * проверка не отработала из-за ошибки, писать человеку „не смогли проверить“,
 * а не „нет“, „не подключено“ или пустой список».
 *
 * Класс дефекта один и тот же в трёх местах продукта: запрос не удался, а
 * кабинет объявляет отрицательный ответ и уводит человека чинить то, что
 * исправно.
 *  - WEB-1523 (закрывается этим атомом): отказ запроса о подключении Stripe
 *    читался как «платёжка не подключена» → блоки оплаты помечались
 *    неработающими и «Опубликовать» показывала ложную причину.
 *  - WEB-1360 остаток 1 (следующая пачка): сбой запроса доменов даёт
 *    `boundDomainCount = 0` → кнопка «Опубликовать» гаснет с ложным
 *    «Привяжите домен». Ложится сюда: `answerOrUnknown` не булев, а
 *    обобщённый — число доменов проходит через него так же.
 *  - WEB-1518 (следующая пачка): упавший запрос истории транзакций рисует
 *    «Транзакций пока нет». Ложится сюда: `checkStateFromError` даёт ветку
 *    ошибки, `isCheckFailed` ставит её ПЕРЕД проверкой на пустоту.
 * ⚠ Файлы WEB-1360 и WEB-1518 этим атомом сознательно не трогаются — только
 * помощник и его канон.
 *
 * Словарь состояний не новый: это ратифицированный в продукте канон
 * `'loading' | 'idle' | 'error' | 'forbidden'` (ProductsTab.tsx:92,
 * CustomDomainSection.tsx:61, CustomerDetail.tsx:87,
 * CustomerEventTimeline.tsx:25). Модуль его не переизобретает, а собирает в
 * одном месте — до этого у каждого экрана жила своя копия правила, и копии
 * расходились (в useStripePrices послабление было сделано только для 403).
 *
 * Почему `forbidden` отделён от `error`: «вам не показали» и «мы не смогли
 * спросить» — разные тексты для человека (см. CustomDomainSection:
 * «Недостаточно прав» против «Не удалось загрузить»). Для ОТВЕТА разницы нет:
 * оба — не ответ.
 */
import { extractApiError } from '@/api/errorStatus';

/**
 * Состояние проверки.
 * - `loading` — идёт, ответа ещё нет;
 * - `idle` — сервер ответил, ответ достоверен;
 * - `error` — не смогли проверить (сеть, 5xx, 404, тайм-аут, 429);
 * - `forbidden` — состояние не показали (403).
 * Достоверный ответ несёт ТОЛЬКО `idle`.
 */
export type CheckState = 'loading' | 'idle' | 'error' | 'forbidden';

/**
 * Отказ запроса → состояние проверки. 403 — «не показали», всё остальное —
 * «не смогли проверить». Ответом «нет» отказ не становится никогда.
 */
export function checkStateFromError(error: unknown): 'error' | 'forbidden' {
  return extractApiError(error).status === 403 ? 'forbidden' : 'error';
}

/** Проверка не отработала — показывать «не смогли проверить», а не «нет». */
export function isCheckFailed(state: CheckState): boolean {
  return state === 'error' || state === 'forbidden';
}

/**
 * Единственный разрешённый путь от проверки к ответу: достоверный ответ
 * отдаётся как есть (в том числе честное `false` или честный `0`), любое
 * другое состояние — `undefined`, то есть «не знаем».
 *
 * Смысл именно в типе: интерфейс, который различает `false` и `undefined`,
 * физически не может напечатать «не подключено» по неудавшемуся запросу.
 */
export function answerOrUnknown<T>(
  state: CheckState,
  value: T,
): T | undefined {
  return state === 'idle' ? value : undefined;
}
